import React, { FC, useCallback, useEffect, useState } from "react";
import { Dialog, DialogActionProps } from "react-toolbox/lib/dialog"; // TODO: migrate
import { Alert, Input, LoginFormData, LoginForm, PasswordInput, PasswordRequirements } from "@xapp/components";

import { classNames } from "../../../utils/classNames";

import { decodeJwtPayload } from "auth/utils/decodeJwt";
import {
    fetchAuthSession,
    resendSignUpCode,
    signUp,
    signInWithRedirect,
    confirmSignUp,
    signIn,
    SignUpInput,
} from "aws-amplify/auth";

import { useBoolState } from "utils/bool";
import { AuthSetTokenProps as SignUpSession } from "auth";

import style from "./style.scss";
import { configureAmplifyAuth } from "auth/utils/configureAmplify";

export { SignUpSession };

declare global {
    interface Window {
        FB: any;
    }
}

type FacebookLoginStatus = "connected" | "not_authorized" | "unknown";

interface FacebookAuthResponse {
    accessToken: string;
    expiresIn: number;
    signedRequest: string;
    userID: string;
}

interface FacebookLoginStatusResponse {
    status: FacebookLoginStatus;
    authResponse?: FacebookAuthResponse;
}

interface FacebookUser {
    id: string;
    name?: string;
    email?: string;
}

/**
 * Determine if the user is using the Facebook in-app browser.
 *
 * @returns
 */
const useIsFacebookBrowser = (): boolean => {
    const [isFacebookBrowser, setIsFacebookBrowser] = React.useState(false);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore window.opera doesn't exist by default
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        // Detect Facebook in-app browser
        const isFB = /FBAV|FBAN/.test(userAgent);
        setIsFacebookBrowser(isFB);
    }, []);

    return isFacebookBrowser;
};

export interface errors {
    code?: string;
    name?: string;
    message?: string;
    stack?: string;
}

export interface SignUpProps {
    /**
     * Optional data to prefill the form with
     */
    data?: Partial<LoginFormData>;
    /**
     * Optional title for the form
     */
    title?: string;
    /**
     * Optional subtitle for the form
     */
    subtitle?: string;
    /**
     * Optional site key for recaptcha
     */
    recaptchaSiteKey?: string;
    /**
     * Should the verification code dialog be active.
     */
    showVerificationCodeDialog?: boolean;
    /**
     * Optional class to be added to the main component
     */
    className?: string;
    /**
     * Display the social logins
     */
    displaySocialLogins?: boolean;
    /**
     * Optional password requirements that are passed through to the PasswordInput
     */
    passwordRequirements?: PasswordRequirements;
    /**
     * User signed up but not verified yet
     */
    onSignUpSuccess?(data: LoginFormData): void;
    /**
     * User signed up and verified
     */
    onCodeVerified?(data: LoginFormData): void;
    /**
     * User has successfully signed in
     */
    onSignIn?(session: SignUpSession, data: LoginFormData): void;
}

export const SignUp: FC<SignUpProps> = (props) => {
    const {
        data: initialData,
        className,
        displaySocialLogins,
        recaptchaSiteKey,
        showVerificationCodeDialog,
        onSignUpSuccess,
        onCodeVerified,
        onSignIn,
        title,
        subtitle,
        passwordRequirements,
    } = props;

    const isFacebookBrowser = useIsFacebookBrowser();

    // turning it off for now
    const displayFacebook = false; // displaySocialLogins;
    // don't display google on facebook browser
    const displayGoogle = displaySocialLogins && !isFacebookBrowser;

    const [confirmCodeDialogActive, hideCodeDialog, showCodeDialog] = useBoolState(showVerificationCodeDialog);

    const [suspectedBot, setSuspectedBot] = useState<boolean>(false);
    const [disableClicked, setDisableClicked] = useState<boolean>(false);

    const [data, setData] = useState<LoginFormData>({ ...initialData });
    const [code, setCode] = useState<string>("");
    // if they refreshed the page, we lost the password and we don't want to save it locally so we
    // require they enter it again when confirming
    const [password, setPassword] = useState<string>("");

    const [errorSignUp, setErrorSignUp] = useState<errors | undefined>(undefined);

    const [successMessage, setSuccessMessage] = useState<string>();

    useEffect(() => {
        if (successMessage) {
            setTimeout(() => {
                setSuccessMessage("");
            }, 2000);
        }
    }, [successMessage, setSuccessMessage]);

    useEffect(() => {
        // Same helper the passwordless flow uses, so the two cannot disagree
        // about which pool Studio talks to (#5459).
        //
        // Logged rather than shown, on purpose and unlike the magic-link path:
        // an Auth0 account (stentor.production) configures no Cognito pool at
        // all, and this form's own sign-up there does not need one. Showing the
        // operator error would put a failure on a page that works. The pool is
        // only actually required once an Amplify call is made, and those raise
        // it where it matters.
        try {
            configureAmplifyAuth();
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        // Ensure the reCAPTCHA script is loaded
        window.grecaptcha &&
            window.grecaptcha.ready(() => {
                console.log("reCAPTCHA ready!");
            });
    }, []);

    async function _signUp(email: string, password: string, name: string, website?: string) {
        try {
            if (!name) {
                setErrorSignUp({
                    message: "Name must not be empty.",
                });
                return;
            }

            // split name into first and last if greater than 1 word
            const [firstName, ...lastName] = name.split(" ");
            const family_name = lastName.join(" ");

            const input: SignUpInput = {
                username: email,
                password,
                options: {
                    userAttributes: {
                        email,
                        name: firstName,
                    },
                },
            };

            if (website) {
                input.options.userAttributes.website = website;
            }

            if (family_name) {
                input.options.userAttributes.family_name = family_name;
            }

            await signUp(input);

            // Successful sign up
            if (typeof onSignUpSuccess === "function") {
                onSignUpSuccess({ email, password, website, firstName: name, lastName: family_name });
            }
            setData({ email, password, website, firstName: name, lastName: family_name });
            showCodeDialog();
        } catch (error) {
            setErrorSignUp(error);
        }
    }

    async function resendSignUp(email: string) {
        // i think this is right?
        await resendSignUpCode({ username: email })
            .then(() => {
                setSuccessMessage("New code sent.");
            })
            .catch((err) => {
                console.error(err);
            });
    }

    async function verifyCodeAndLogin() {
        // Verify try-catch
        let error: Error;
        try {
            const verify = await confirmSignUp({
                username: data.email,
                confirmationCode: code,
            });

            if (verify.isSignUpComplete) {
                // Login!
                if (typeof onCodeVerified === "function") {
                    onCodeVerified(data);
                }

                setSuccessMessage("User confirmed.");
                hideCodeDialog();
            } else {
                throw new Error("Invalid code, please try again.");
            }
        } catch (e) {
            if (e.message.includes("Current status is CONFIRMED")) {
                // not error case, log them in!
                if (typeof onCodeVerified === "function") {
                    onCodeVerified(data);
                }

                setSuccessMessage("User already confirmed.");
                hideCodeDialog();
            } else {
                error = e;
                console.log("error confirming sign-up:", error);
                setErrorSignUp(error);
            }
        }

        // if no error
        if (error) {
            return;
        }

        // SignIn and Session (tokens)
        try {
            await signIn({ username: data.email, password: data.password || password });

            const session = await fetchAuthSession();

            const { idToken, accessToken } = session.tokens;

            // Decode the JWT and retrieve the expiration time. Shared helper
            // because JWT segments are base64url, which atob cannot read on its
            // own — see decodeJwt.ts.
            const { exp: expiresInSeconds } = decodeJwtPayload(idToken.toString());

            if (typeof onSignIn === "function") {
                onSignIn({ accessToken: accessToken.toString(), expiresInSeconds }, data);
            }
        } catch (e) {
            setErrorSignUp(e);
        }
    }

    const handleSubmit = (data: LoginFormData) => {
        console.log("submitting:", data);
        _signUp(data.email, data.password, data.fullName, data.website);
    };

    const handleSendNewCode = useCallback(() => {
        resendSignUp(data.email);
    }, [data]);

    const handleDisabledClick = useCallback(async () => {
        setDisableClicked(true);
        // Execute reCAPTCHA

        if (window.grecaptcha && recaptchaSiteKey) {
            const token: string = await window.grecaptcha.execute(recaptchaSiteKey, {
                action: "disabled_create_account",
            });

            // Send the token to your server for verification
            const captcha: { success: boolean; score: number } = await fetch(
                "https://46fkqffn7brkr6uwr5z5uhh3ru0exrje.lambda-url.us-east-1.on.aws/",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ token }),
                }
            )
                .then((response) => response.json())
                .catch((error) => console.error("Error when requesting reCaptcha:", error));

            if (captcha) {
                if (captcha.success && captcha.score > 0.5) {
                    console.log("reCAPTCHA verified");
                } else {
                    console.log("reCAPTCHA not verified, suspected bot.");
                    setSuspectedBot(true);
                }
            }
        } else {
            console.log("reCAPTCHA not available or ready");
        }
    }, [recaptchaSiteKey]);

    const handleGoogleSubmit = async () => {
        // save current path and query string to local storage for redirect after login
        const currentPathWithQuery = window.location.pathname + window.location.search;
        localStorage.setItem("xa_redirect", currentPathWithQuery);
        try {
            // we eventually want to migrate over to calling this directly
            // https://docs.aws.amazon.com/cognito/latest/developerguide/authorization-endpoint.html
            await signInWithRedirect({ provider: "Google" });
        } catch (e) {
            // if we get the
            console.log(e);
            setErrorSignUp(e.message);
        }
    };

    const handleFacebookSubmit = async () => {
        if (isFacebookBrowser) {
            function initFacebookSdk() {
                window.FB.init({
                    appId: 682438770632962,
                    cookie: true,
                    xfbml: false,
                    version: "v19.0",
                });

                window.FB.getLoginStatus(async function (response: FacebookLoginStatusResponse) {
                    if (response.status === "connected") {
                        // token is available, now what?
                        const fbToken = response.authResponse.accessToken;
                        // const fbExpiresIn = response.authResponse.expiresIn;

                        // Prompt user for email if needed
                        window.FB.api("/me?fields=email,name", async (fbUser: FacebookUser) => {
                            const email = fbUser.email;
                            const name = fbUser.name;

                            if (!email) {
                                alert(
                                    "We couldn’t get your email from Facebook. Please sign up manually or use a different login method."
                                );
                                return;
                            }

                            // Send user data and token to your backend to register or log them in manually
                            // Or: call Amplify Auth.signIn() if you're ready to integrate Identity Pool fallback
                            console.log({ fbToken, email, name });

                            // TEMP fallback: redirect to your hosted UI to complete flow
                            window.location.href = `https://${
                                window.xaStudioConfig?.auth?.domain
                            }/oauth2/authorize?identity_provider=Facebook&response_type=code&client_id=${
                                window.xaStudioConfig?.auth?.userWebPoolClientId
                            }&redirect_uri=${encodeURIComponent(window.location.origin + "/authorize")}`;
                        });
                    } else {
                        window.FB.login();
                    }
                });
            }

            // Load Facebook SDK if not already loaded
            if (!window.FB) {
                const script = document.createElement("script");
                script.src = "https://connect.facebook.net/en_US/sdk.js";
                script.async = true;
                script.onload = initFacebookSdk;
                document.body.appendChild(script);
            } else {
                initFacebookSdk();
            }
        } else {
            // ✅ In normal browser, just use Cognito redirect
            const currentPathWithQuery = window.location.pathname + window.location.search;
            localStorage.setItem("xa_redirect", currentPathWithQuery);
            try {
                await signInWithRedirect({ provider: "Facebook" });
            } catch (e) {
                console.log(e);
                setErrorSignUp(e.message);
            }
        }
    };

    const dialogActions: DialogActionProps[] = [
        {
            raised: true,
            label: "Send New Code",
            onClick: handleSendNewCode,
        },
        {
            raised: true,
            primary: true,
            label: "Confirm Code",
            onClick: verifyCodeAndLogin,
        },
    ];

    return (
        <>
            <div style={{ width: "100%" }} className={className}>
                <LoginForm
                    className={style.loginForm}
                    initialData={{ ...data }}
                    urlSignIn={"/login"}
                    title={title}
                    subtitle={subtitle}
                    passwordRequirements={passwordRequirements}
                    hideFields={{
                        firstName: true,
                        lastName: true,
                        website: true,
                    }}
                    footer={
                        <div className={style.footerWrapper}>
                            {recaptchaSiteKey && (
                                <p className={style.recapthcaNotice}>
                                    This site is protected by reCAPTCHA and the Google{" "}
                                    <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
                                    <a href="https://policies.google.com/terms">Terms of Service</a> apply.
                                </p>
                            )}
                            <p className={classNames(style.smallWarning, { [style.disabled]: !disableClicked })}>
                                Please fill out the fields to create an account
                            </p>
                        </div>
                    }
                    onDisabledClick={handleDisabledClick}
                    onSubmit={handleSubmit}
                    onGoogleSubmit={displayGoogle ? handleGoogleSubmit : undefined}
                    onFacebookSubmit={displayFacebook ? handleFacebookSubmit : undefined}
                />
            </div>

            <Alert
                handleClose={() => setSuspectedBot(false)}
                open={suspectedBot}
                text="We suspect you might be a bot.  Fill out all the information and try again."
                severity="error"
            />

            <Alert
                handleClose={() => setErrorSignUp(undefined)}
                open={!!errorSignUp}
                text={errorSignUp?.message || "An error occurred."}
                severity="error"
            />

            <Alert
                handleClose={() => setSuccessMessage("")}
                open={!!successMessage}
                text={successMessage}
                severity="success"
            />

            <Dialog
                title="Confirm Code"
                actions={dialogActions}
                active={confirmCodeDialogActive}
                style={{ padding: 20, height: 50 }}
            >
                <p style={{ margin: "20px 0px" }}>
                    We have sent you a verification code by email from support@xapp.ai (might need to check your SPAM),
                    please enter it below
                </p>
                <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                    <Input label="Code" variation="outlined" onChange={(value: string) => setCode(value)} />
                    {!data.password ? (
                        <PasswordInput
                            label="Password"
                            variation="outlined"
                            helperText="Please re-enter your password for security."
                            onChange={(value: string) => setPassword(value)}
                        />
                    ) : (
                        <></>
                    )}
                </div>
            </Dialog>
        </>
    );
};
